/**
 * Equipment presets — starting points for the brewer's own equipment profiles
 * (PLAN §11, M4: "equipment profile presets library").
 *
 * ## The handshake with `@beerwright/core`
 *
 * This package does not depend on core, so the loss/rate fields below are
 * **declared here and mirrored by hand** onto core's `EquipmentProfile` — the
 * same deliberate handshake `maltClass` already makes: the catalog states the
 * numbers, core owns the arithmetic that consumes them. Field names, units and
 * meanings are identical on both sides on purpose, so the mapping in the app is
 * a field-for-field copy with no conversion, and TypeScript catches a drifting
 * field as a missing property rather than a silently absent loss.
 *
 * ## Provenance
 *
 * Almost every entry is `representative`, and that is the honest answer.
 * Makers publish vessel capacities, maximum grain bills and element wattages;
 * a few also publish a boil-off or a basket deadspace. Those published figures
 * are used when they exist. Everything else — evaporation on a lid-off boil,
 * pickup losses, grain absorption — depends on the room and the brewer, so
 * the row stays class-typical. What ships here are the figures a brewer
 * should *start* from and then recalibrate from their first few batches —
 * which is what the profile's `notes` say too.
 *
 * A preset is a **template, not a reference**. Creating a profile from one
 * copies the numbers into the workspace; later corrections here never move an
 * existing brewer's profile, for the same reason recipes snapshot ingredients.
 */

import { z } from 'zod';
import { descriptionSchema, provenanceSchema, slugSchema } from './common';

/**
 * Lautering strategy. Mirrors core's `SpargeType` exactly.
 *
 * `biab-full-volume` and `no-sparge` both mash in the whole liquor; they are
 * separate because a BIAB bag gives water back when squeezed and a no-sparge
 * lauter tun does not.
 */
export const equipmentSpargeTypeSchema = z.enum(['batch', 'fly', 'no-sparge', 'biab-full-volume']);

/** @see equipmentSpargeTypeSchema */
export type EquipmentSpargeType = z.infer<typeof equipmentSpargeTypeSchema>;

/** A ready-made equipment profile for a common brewing system. */
export const equipmentPresetSchema = z.strictObject({
  /** Stable slug, e.g. `grainfather-g30`. */
  id: slugSchema,
  /** Display name, e.g. `Grainfather G30`. */
  name: z.string().min(3).max(60),
  /** Who makes it; `Generic` for the class entries that belong to no maker. */
  maker: z.string().min(2).max(40),
  /** Two or three sentences in our own words. */
  description: descriptionSchema,
  /** What the brewer should recalibrate first, and why these numbers are what they are. */
  notes: z.string().min(20).max(600),
  /**
   * The batch volume into the fermenter this system is designed around,
   * **litres**. Not a limit — it is what the preset browser shows and what the
   * volume waterfall previews against.
   */
  nominalBatchVolumeL: z.number().positive().max(1000),
  /** Search/filter tags: `all-in-one`, `electric`, `biab`, `nano`. */
  tags: z.array(z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)).max(12),
  /** @see provenanceSchema */
  provenance: provenanceSchema,

  // --- mirrored from core's EquipmentProfile, field for field ---------------

  /** @see equipmentSpargeTypeSchema */
  spargeType: equipmentSpargeTypeSchema,
  /**
   * Volume below the mash tun outlet / false bottom that returns to the kettle
   * (all-in-one systems), **litres**. Does not change water requirements.
   */
  mashTunDeadspaceL: z.number().min(0).max(100),
  /** Wort genuinely left behind in the mash tun, **litres**. Adds to the water needed. */
  mashTunLossL: z.number().min(0).max(100),
  /** Water retained by spent grain, **litres per kilogram**. */
  grainAbsorptionLPerKg: z.number().min(0).max(5),
  /** Default mash thickness, **litres per kilogram of grain**. */
  mashThicknessLPerKg: z.number().positive().max(20),
  /** Evaporation at a rolling boil, **litres per hour**. */
  boilOffRateLPerHour: z.number().min(0).max(100),
  /** Thermal contraction from boiling to 20 °C, **%** of the hot volume. */
  boilShrinkagePercent: z.number().min(0).max(100),
  /** Wort left in the kettle below the pickup, **litres**. */
  kettleDeadspaceL: z.number().min(0).max(100),
  /** Wort retained by kettle hops, **mL/g** (≡ L/kg). */
  hopAbsorptionMlPerG: z.number().min(0).max(20),
  /** Wort retained by dry hops in the fermenter, **mL/g**. */
  dryHopAbsorptionMlPerG: z.number().min(0).max(20),
  /** Wort left in the chiller and hoses, **litres**. */
  chillerLossL: z.number().min(0).max(100),
  /** Beer left on the trub/yeast cake, **litres**. */
  fermenterTrubLossL: z.number().min(0).max(100),
  /** Water added to the kettle after lautering, **litres**. */
  topUpKettleL: z.number().min(0).max(1000),
  /** Water added to the fermenter after transfer, **litres**. */
  topUpFermenterL: z.number().min(0).max(1000),
  /** Mash efficiency, **%**. */
  mashEfficiencyPercent: z.number().min(0).max(100),
  /** Brewhouse efficiency, **%** — informational on input. */
  brewhouseEfficiencyPercent: z.number().min(0).max(100),
  /** Mash tun thermal mass as an equivalent **litres of water**. */
  mashTunThermalMassLEquivalent: z.number().min(0).max(100),
  /** Mash tun temperature at dough-in, **°C**. */
  mashTunInitialTempC: z.number().min(-20).max(110),
  /** Kettle capacity, **litres**, or `null` when the system does not fix one. */
  kettleVolumeL: z.number().positive().max(10_000).nullable(),
  /** Mash tun capacity, **litres**, or `null`. */
  mashTunVolumeL: z.number().positive().max(10_000).nullable(),
  /**
   * How fast the resting mash loses heat, **°C per hour**.
   *
   * Only decoction reads it, and there it is the largest correction the
   * technique needs — a thick pull is away from the tun for the better part of
   * an hour. It is a property of the *vessel*, which is why it belongs on a rig
   * preset at all, and it is **required** rather than defaulted so that adding
   * a system to this catalog forces the question rather than silently inheriting
   * a cooler's figure.
   *
   * Four bands cover every system here, and the file uses no other value:
   *
   * | °C/h | The vessel |
   * | --- | --- |
   * | 0 | Heated while the mash rests — every all-in-one, eBIAB and RIMS/HERMS. The element or the coil holds the setpoint, so there is nothing to make up. |
   * | 1 | A large jacketed commercial vessel. Little to do with the jacket and much to do with surface area per litre: a 1 bbl tun has far less of it than a 20 L one. |
   * | 1.5 | A purpose-built foam-insulated tun with no heat source in it. |
   * | 2 | A beverage-cooler mash tun — the classic, and the engine's own default. |
   * | 4 | A bare kettle with the burner off under it. |
   *
   * The decoction pot and its boil-off are deliberately **not** here: no system
   * in this catalog ships one, and the pot a brewer decocts into is their own.
   */
  mashStandingLossCPerHour: z.number().min(0).max(50),
});

/** @see equipmentPresetSchema */
export type EquipmentPreset = z.infer<typeof equipmentPresetSchema>;
